import { Button, Tooltip } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { Icon, toastNotifications, variants } from '@uag/react-core';
import {
    getGetRequestDetailQueryKey,
    getGetRequestsQueryKey,
    useVerifyRequest,
} from '@uag/welcome-shared/src/api/queries/requests/requests';
import { MAX_STREET_LENGTH } from '@uag/welcome-shared/src/components/company/CompanyForm/companyFormSchema';
import { useTranslation } from 'react-i18next';

import { useCurrentContributorContext } from 'app/CurrentContributorProvider';
import { openApproveExistingCustomerModal, openApproveIndustrialSectorModal } from '../modals/ApproveModal';
import { useRequestContext } from '../RequestProvider';
import { checkForDokaResponsibleChange } from './functions';
import { RequestActionButtonProps } from './types';

export const ApproveRequestAction = (_: RequestActionButtonProps) => {
    const { t } = useTranslation();
    const { mutateAsync: verifyRequest, isPending: isVerifyPending } = useVerifyRequest();
    const queryClient = useQueryClient();
    const { request } = useRequestContext();
    const { currentContributor } = useCurrentContributorContext();

    const street = request.companyData.mainAddress?.street ?? '';
    const isStreetTooLong = street.length > MAX_STREET_LENGTH;
    const isApproveDisabled = isStreetTooLong;

    const handleApprove = async () => {
        if (await checkForDokaResponsibleChange(request, currentContributor)) {
            return;
        }

        if (request.existingCustomer) {
            try {
                await openApproveExistingCustomerModal({
                    userName: request.administrators?.[0]?.model.displayName ?? undefined,
                    email: request.administrators?.[0]?.model.email ?? undefined,
                    companyName: request.companyData.companyName ?? undefined,
                });
            } catch {
                return;
            }
        }

        let industrialSectorIdentifier: string | undefined;
        try {
            if (request.existingCustomer && !request.companyData.industrialSectorIdentifier) {
                const { industrialSector } = await openApproveIndustrialSectorModal();
                industrialSectorIdentifier = industrialSector;
            }
        } catch {
            return;
        }
        try {
            await verifyRequest({
                id: request.id,
                data: { verifyState: 'Accept', industrialSectorIdentifier },
            });

            toastNotifications.success({
                title: t('success'),
                message: t('requestActionSuccess', { action: t('approved') }),
            });

            await queryClient.invalidateQueries({ queryKey: getGetRequestDetailQueryKey(request.id) });
            await queryClient.invalidateQueries({ queryKey: getGetRequestsQueryKey() });
        } catch (error) {
            toastNotifications.errorDetail(
                {
                    title: t('failed'),
                    message: t('requestActionFailed', {
                        action: t('approved'),
                    }),
                },
                error,
            );
        }
    };

    return (
        <Tooltip disabled={!isApproveDisabled} label={t('addressDataNeedsReview')} variant={variants.tooltip.custom}>
            <Button disabled={isApproveDisabled} loading={isVerifyPending} type="submit" onClick={handleApprove}>
                {t('approve')}
                <Icon className="ml-base" icon="check" />
            </Button>
        </Tooltip>
    );
};
